import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useUiStore } from '@store/uiStore';
import { useAuth } from '@auth/AuthContext';
import { buildNavItems, findActiveNavItem, type NavItem } from './navItems';
import { NvrLogoMark } from './icons';

const ITEM_HEIGHT = 48;

interface SidebarItemProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onActivate: (item: NavItem) => void;
}

function SidebarItem({ item, active, collapsed, onActivate }: SidebarItemProps): JSX.Element {
  const interactive = item.enabled;
  const ariaLabel = collapsed ? item.label : undefined;
  const title = collapsed
    ? item.enabled
      ? item.label
      : `${item.label} — ${item.reason ?? 'unavailable'}`
    : !item.enabled
      ? (item.reason ?? 'unavailable')
      : undefined;

  return (
    <li>
      {item.separated ? (
        <div
          aria-hidden
          role="separator"
          className="mx-3 my-2 h-px"
          style={{ backgroundColor: 'var(--app-border)' }}
        />
      ) : null}
      <button
        type="button"
        disabled={!interactive}
        aria-current={active ? 'page' : undefined}
        aria-label={ariaLabel}
        title={title}
        onClick={() => interactive && onActivate(item)}
        className={clsx(
          'group flex w-full items-center gap-4 px-4 text-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
          'disabled:cursor-not-allowed disabled:opacity-50',
          collapsed && 'justify-center px-0',
        )}
        style={{
          height: ITEM_HEIGHT,
          color: active ? 'var(--app-sidebar-icon-active)' : 'var(--app-sidebar-icon)',
          backgroundColor: active ? 'var(--app-sidebar-active-bg)' : 'transparent',
          borderLeft: active ? '3px solid var(--app-accent)' : '3px solid transparent',
          paddingLeft: collapsed ? 0 : 13,
        }}
        onMouseEnter={(e) => {
          if (!active && interactive) {
            e.currentTarget.style.backgroundColor = 'var(--app-sidebar-hover)';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span aria-hidden className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
          {item.icon}
        </span>
        {!collapsed && <span className="truncate">{item.label}</span>}
      </button>
    </li>
  );
}

interface AppSidebarProps {
  productInitials?: string;
  /** Invoked when an action item (e.g. Logout) is activated. Optional for now; wired up once Auth ships. */
  onAction?: (itemId: string) => void;
}

/**
 * Global sidebar per Jira OOM-1256.
 *
 * - Collapsed/expanded state lives in the persisted `useUiStore`.
 * - Logo slot at top is always centered; expanded state shows product short
 *   form next to it.
 * - Items source from `navItems.tsx` (single source of truth).
 * - Disabled items render with a tooltip explaining why.
 */
export function AppSidebar({ productInitials = 'NVR', onAction }: AppSidebarProps): JSX.Element {
  const { sidebarCollapsed } = useUiStore();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const navItems = useMemo(
    () =>
      buildNavItems({
        canManageUsers: auth.hasPermission('user_management'),
        canManageRoles: auth.me?.roleSlug === 'super_admin',
      }),
    [auth],
  );

  const active = findActiveNavItem(location.pathname, navItems);

  const handleActivate = useCallback(
    (item: NavItem): void => {
      if (item.isAction) {
        if (item.id === 'logout') {
          void auth.logout().then(() => navigate('/login', { replace: true }));
        }
        onAction?.(item.id);
        return;
      }
      if (item.path) navigate(item.path);
    },
    [auth, navigate, onAction],
  );

  return (
    <aside
      aria-label="Primary navigation"
      className="flex h-full min-h-0 flex-col overflow-hidden"
      style={{
        width: sidebarCollapsed
          ? 'var(--app-sidebar-w-collapsed)'
          : 'var(--app-sidebar-w-expanded)',
        backgroundColor: 'var(--app-sidebar-bg)',
        color: 'var(--app-sidebar-icon)',
        transition: 'width 180ms ease',
      }}
    >
      <div
        className={clsx(
          'flex items-center gap-2 border-b px-4',
          sidebarCollapsed && 'justify-center px-0',
        )}
        style={{ height: 'var(--app-header-h)', borderColor: 'var(--app-border)' }}
      >
        <NvrLogoMark size={28} style={{ color: 'var(--app-primary)' }} />
        {!sidebarCollapsed && (
          <span
            className="text-sm font-semibold tracking-wide"
            style={{ color: 'var(--app-text-primary)' }}
          >
            {productInitials}
          </span>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        <ul className="flex flex-col">
          {navItems.map((item) => (
            <SidebarItem
              key={item.id}
              item={item}
              active={active?.id === item.id}
              collapsed={sidebarCollapsed}
              onActivate={handleActivate}
            />
          ))}
        </ul>
      </nav>
    </aside>
  );
}
